// src/utils/validators.ts
import type { FieldErrors } from "../wizard/wizardTypes";
import type {
  ParentInfoState,
  VolunteerInfoState,
  ChildState,
  EmergencyContactState,
} from "../wizard/wizardState";

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isPhone(value: string) {
  const digits = value.replace(/[^\d]/g, "");
  return digits.length >= 10;
}

function required(value: string) {
  return value.trim().length > 0;
}

function numberInRange(value: string, min: number, max: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= min && parsed <= max;
}

export function validateParentInfo(state: ParentInfoState) {
  const errors: FieldErrors = {};
  if (!required(state.firstName)) errors.firstName = "First name is required";
  if (!required(state.lastName)) errors.lastName = "Last name is required";
  if (!isEmail(state.email)) errors.email = "Valid email is required";
  if (!isPhone(state.phone)) errors.phone = "Valid phone is required";
  if (!required(state.relationToChild)) errors.relationToChild = "Relation is required";
  if (!required(state.pickupType)) errors.pickupType = "Pickup type is required";
  if (state.passphrase.trim().length < 1) errors.passphrase = "Field is required";
  return { isValid: Object.keys(errors).length === 0, errors };
}

export function validateVolunteerInfo(
  state: VolunteerInfoState,
  requirePassword: boolean,
  requirePickupType: boolean
) {
  const errors: FieldErrors = {};
  if (!required(state.firstName)) errors.firstName = "First name is required";
  if (!required(state.lastName)) errors.lastName = "Last name is required";
  if (!isEmail(state.email)) errors.email = "Valid email is required";
  if (!isPhone(state.phone)) errors.phone = "Valid phone is required";

  if (!required(state.street)) errors.street = "Street is required";
  if (!required(state.city)) errors.city = "City is required";
  if (!required(state.state)) errors.state = "State is required";
  if (!required(state.zip)) errors.zip = "Zip is required";

  if (!required(state.ageGroup))
    errors.ageGroup = "Age group is required";
  if (state.ageGroup === "under18" && !required(state.grade))
    errors.grade = "Grade is required for volunteers under 18";

  if (!required(state.tshirtSize)) errors.tshirtSize = "Tshirt size is required";
  if (!required(state.church)) errors.church = "Church is required";
  if (state.availabilityDays.length === 0)
    errors.availabilityDays = "Select at least one available day";

  if (requirePickupType) {
    if (!required(state.pickupType))
      errors.pickupType = "Pickup type is required";
    if (!required(state.relationToChild))
      errors.relationToChild = "Relation to child is required";
  }

  if (requirePassword) {
    if (state.passphrase.trim().length < 0) errors.passphrase = "Field is required";
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

export function validateVolunteers(
  volunteers: VolunteerInfoState[],
  requirePassword: boolean,
  requirePickupType: boolean
) {
  const errors: FieldErrors = {};
  if (volunteers.length === 0) {
    errors.volunteers = "At least one volunteer is required";
    return { isValid: false, errors };
  }
  volunteers.forEach((v, idx) => {
    const pfx = `volunteer_${idx}`;
    if (!required(v.firstName)) errors[`${pfx}_firstName`] = "First name is required";
    if (!required(v.lastName)) errors[`${pfx}_lastName`] = "Last name is required";
    if (!isEmail(v.email)) errors[`${pfx}_email`] = "Valid email is required";
    if (!isPhone(v.phone)) errors[`${pfx}_phone`] = "Valid phone is required";
    if (!required(v.street)) errors[`${pfx}_street`] = "Street is required";
    if (!required(v.city)) errors[`${pfx}_city`] = "City is required";
    if (!required(v.state)) errors[`${pfx}_state`] = "State is required";
    if (!required(v.zip)) errors[`${pfx}_zip`] = "Zip is required";
    if (!required(v.ageGroup)) errors[`${pfx}_ageGroup`] = "Age group is required";
    if (v.ageGroup === "under18" && !required(v.grade))
      errors[`${pfx}_grade`] = "Grade is required for volunteers under 18";
    if (!required(v.tshirtSize)) errors[`${pfx}_tshirtSize`] = "Tshirt size is required";
    if (!required(v.church)) errors[`${pfx}_church`] = "Church is required";
    if (v.availabilityDays.length === 0)
      errors[`${pfx}_availabilityDays`] = "Select at least one available day";
    if (requirePickupType) {
      if (!required(v.pickupType)) errors[`${pfx}_pickupType`] = "Pickup type is required";
      if (!required(v.relationToChild))
        errors[`${pfx}_relationToChild`] = "Relation to child is required";
    }
    if (requirePassword) {
      if (v.passphrase.trim().length < 1) errors[`${pfx}_passphrase`] = "Field is required";
    }
  });
  return { isValid: Object.keys(errors).length === 0, errors };
}

export function validateChildren(children: ChildState[]) {
  const errors: FieldErrors = {};
  if (children.length === 0) {
    errors.children = "At least one child is required";
    return { isValid: false, errors };
  }
  children.forEach((c, idx) => {
    if (!required(c.firstName)) errors[`child_${idx}_firstName`] = "Required";
    if (!required(c.lastName)) errors[`child_${idx}_lastName`] = "Required";
    if (!numberInRange(c.age, 1, 13)) errors[`child_${idx}_age`] = "Age 1-13";
    if (!required(c.grade)) errors[`child_${idx}_grade`] = "Grade is required";
    if (!required(c.tshirtSize))
      errors[`child_${idx}_tshirtSize`] = "Tshirt size is required";
  });
  return { isValid: Object.keys(errors).length === 0, errors };
}

export function validateEmergencyContacts(contacts: EmergencyContactState[]) {
  const errors: FieldErrors = {};
  if (contacts.length === 0) {
    errors.emergencyContacts = "At least one emergency contact is required";
    return { isValid: false, errors };
  }
  contacts.forEach((c, idx) => {
    if (!required(c.firstName)) errors[`em_${idx}_firstName`] = "Required";
    if (!required(c.lastName)) errors[`em_${idx}_lastName`] = "Required";
    if (!isPhone(c.phone)) errors[`em_${idx}_phone`] = "Valid phone required";
    if (!required(c.relation)) errors[`em_${idx}_relation`] = "Required";
  });
  return { isValid: Object.keys(errors).length === 0, errors };
}
